import { CssBaseline, ThemeProvider, type PaletteMode } from "@mui/material";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { buildTheme } from "../theme";

const STORAGE_KEY = "colorMode";

interface ColorModeContextValue {
  mode: PaletteMode;
  toggle: () => void;
  setMode: (mode: PaletteMode) => void;
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

function resolveInitialMode(): PaletteMode {
  if (typeof window === "undefined") {
    return "light";
  }
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") {
    return stored;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

interface ColorModeProviderProps {
  children: ReactNode;
}

export function ColorModeProvider({ children }: ColorModeProviderProps) {
  const [mode, setModeState] = useState<PaletteMode>(() => resolveInitialMode());

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, mode);
    document.documentElement.dataset.colorScheme = mode;
  }, [mode]);

  // Follow system changes ONLY when the user hasn't picked a preference yet.
  useEffect(() => {
    if (window.localStorage.getItem(STORAGE_KEY)) {
      return;
    }
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setModeState(e.matches ? "dark" : "light");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const toggle = useCallback(() => {
    setModeState((current) => (current === "light" ? "dark" : "light"));
  }, []);

  const setMode = useCallback((next: PaletteMode) => setModeState(next), []);

  const theme = useMemo(() => buildTheme(mode), [mode]);
  const value = useMemo<ColorModeContextValue>(() => ({ mode, toggle, setMode }), [mode, toggle, setMode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export function useColorMode(): ColorModeContextValue {
  const ctx = useContext(ColorModeContext);
  if (!ctx) {
    throw new Error("useColorMode must be used within ColorModeProvider");
  }
  return ctx;
}
